import { useEffect, useState, useCallback } from 'react'
import Joyride, { CallBackProps, STATUS, Step, Styles } from 'react-joyride'
import { useAuth } from '@/contexts/AuthContext'

const TOUR_STORAGE_KEY = 'sycek_onboarding_completed'

interface OnboardingTourProps {
  run?: boolean
  onComplete?: () => void
}

export function OnboardingTour({ run: externalRun, onComplete }: OnboardingTourProps) {
  const { user } = useAuth()
  const [run, setRun] = useState(false)
  const [stepsReady, setStepsReady] = useState(false)

  // Check if user has completed the tour
  useEffect(() => {
    if (externalRun !== undefined) {
      setRun(externalRun)
      // Small delay to ensure page is fully loaded and elements are rendered
      setTimeout(() => {
        setStepsReady(true)
      }, 500)
      return
    }

    // Auto-start for new users who haven't completed the tour
    const hasCompletedTour = localStorage.getItem(TOUR_STORAGE_KEY) === 'true'
    const isNewUser = user && !hasCompletedTour
    
    if (isNewUser) {
      // Small delay to ensure page is fully loaded and elements are rendered
      setTimeout(() => {
        setStepsReady(true)
        setRun(true)
      }, 1500)
    }
  }, [user, externalRun])

  const steps: Step[] = [
    {
      target: '[data-tour="welcome"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">Welcome to Sycek OSINT Platform!</h3>
          <p className="text-gray-300">
            This quick tour will help you get started with the platform. Let's explore the key features together.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="usage-stats"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">Usage Statistics</h3>
          <p className="text-gray-300">
            Monitor your request usage, credits, and activity here. Keep an eye on your limits to ensure uninterrupted access.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '[data-tour="modules"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">Intelligence Modules</h3>
          <p className="text-gray-300">
            Access powerful OSINT tools including Sycek Profiler, SOCINT, GEOINT, and more. Each module offers specialized intelligence gathering capabilities.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: '[data-tour="breachint"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">Sycek Profiler (formerly BreachINT)</h3>
          <p className="text-gray-300">
            Profile usernames, names, government IDs, emails, phones, domains, and companies (with sanctions/corruption checks) while searching breach data for deep investigations.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="navigation"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">Navigation Sidebar</h3>
          <p className="text-gray-300">
            Use the sidebar to quickly navigate between different modules and features. The dashboard is always accessible from here.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="profile"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">User Profile</h3>
          <p className="text-gray-300">
            Access your profile, settings, API documentation, and logout from the user menu in the top right corner.
          </p>
        </div>
      ),
      placement: 'left',
    },
    {
      target: '[data-tour="activity"]',
      content: (
        <div className="space-y-2">
          <h3 className="text-lg font-bold text-green-400">Activity Logs</h3>
          <p className="text-gray-300">
            View your recent queries, their status, and response times. This helps you track your investigation history.
          </p>
        </div>
      ),
      placement: 'top',
    },
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-green-400">🎉 You're All Set!</h3>
          <p className="text-gray-300">
            You now know the basics of the Sycek OSINT Platform. Start exploring the modules and begin your investigations!
          </p>
          <p className="text-sm text-gray-400">
            You can restart this tour anytime from your profile settings.
          </p>
        </div>
      ),
      placement: 'center',
    },
  ]

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, type } = data
    
    // Handle tour completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      // Mark tour as completed
      localStorage.setItem(TOUR_STORAGE_KEY, 'true')
      setRun(false)
      if (onComplete) {
        onComplete()
      }
    }
    
    // Handle step navigation errors (target not found)
    if (type === 'error:target_not_found') {
      console.warn(`Tour step target not found: ${data.step?.target}`)
      // Joyride will automatically skip to next step or finish if last step
    }
  }, [onComplete])

  const customStyles: Partial<Styles> = {
    options: {
      primaryColor: '#22c55e', // green-500
      textColor: '#ffffff',
      backgroundColor: '#000000',
      overlayColor: 'rgba(0, 0, 0, 0.8)',
      arrowColor: '#22c55e',
      zIndex: 10000,
    },
    tooltip: {
      backgroundColor: '#0a0a0a',
      borderRadius: '8px',
      border: '1px solid #22c55e',
      boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
      padding: '20px',
      maxWidth: '400px',
    },
    tooltipContainer: {
      textAlign: 'left',
    },
    tooltipTitle: {
      color: '#22c55e',
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '8px',
    },
    tooltipContent: {
      padding: '0',
      fontSize: '14px',
      lineHeight: '1.6',
    },
    buttonNext: {
      backgroundColor: '#22c55e',
      color: '#000000',
      borderRadius: '6px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    buttonBack: {
      color: '#9ca3af',
      marginRight: '10px',
      fontSize: '14px',
      padding: '10px 20px',
    },
    buttonSkip: {
      color: '#9ca3af',
      fontSize: '14px',
      padding: '10px 20px',
    },
    buttonClose: {
      color: '#9ca3af',
      fontSize: '20px',
      top: '10px',
      right: '10px',
    },
    spotlight: {
      borderRadius: '8px',
    },
  }

  if (!run || !stepsReady) {
    return null
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={customStyles}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        open: 'Open the dialog',
        skip: 'Skip Tour',
      }}
      floaterProps={{
        disableAnimation: false,
      }}
      disableOverlayClose={false}
      hideCloseButton={false}
      disableScrolling={false}
      scrollOffset={20}
      scrollToFirstStep
    />
  )
}

// Helper function to restart the tour
export function restartOnboardingTour() {
  localStorage.removeItem(TOUR_STORAGE_KEY)
  window.location.reload()
}

// Helper function to check if tour is completed
export function isOnboardingTourCompleted(): boolean {
  return localStorage.getItem(TOUR_STORAGE_KEY) === 'true'
}

